<template>
  <div class="producers">

    <h2 class="producers__title">{{ t('efriends.producersTitle') }}</h2>

    <div class="producers__message" v-if="showResidue">
      <span class="producers__residue">
        {{ t('efriends.residueConsumption') }} {{ residueSupplier }}
        <template v-if="!isBusinessPlan">(brutto): {{ formatPowerPrice(consumptionResidueVatPrice) }} (netto
          {{ formatPowerPrice(consumptionResiduePrice) }})</template>
        <template v-if="(isBusinessPlan || isFlexPlan) && planName">
          <span>{{ planName }}</span>
        </template>
      </span>
      <br />

      <span v-if="
        nextConsumptionResiduePrice !== undefined &&
        !isBusinessPlan &&
        !isFlexPlan
      ">
        {{ nextPriceLabel }}
        {{ formatPowerPrice(nextConsumptionResidueVatPrice) }} (netto
        {{ formatPowerPrice(nextConsumptionResiduePrice) }})
      </span>
    </div>

    <div class="producers__header" v-if="hasEegConnections">
      <div class="producers__title">
        <h3>
          {{ t('efriends.communityConnectionsTitle') }}
          <span class="participation__factor">
            ({{ t('efriends.participationFactor') }}: {{ communityParticipationFactor }}%)
          </span>
        </h3>
      </div>

      <!-- <div class="participation__factor">
        <div class="participation__label">
          {{ t('efriends.participationFactorCommunity') }}
        </div>
        <div class="participation__value">
          {{ communityParticipationFactor }}%
        </div>
      </div> -->
    </div>

    <div data-cy="producers" class="producers__list">
      <transition-group name="producers__list-item" tag="div">
        <div v-for="producer in producers" :key="producer.cubeName" class="producers__list-item" data-cy="producers-item">
          <Producer :producer="producer" />
        </div>
      </transition-group>
    </div>
  </div>

  <div class="producers" v-if="hasEegConnections">
    <div class="producers__header">
      <div class="producers__title">
        <h3>
          {{ t('efriends.eegConnectionsTitle') }}
          <span class="participation__factor">
            ({{ t('efriends.participationFactor') }}: {{ eegParticipationFactor }}%)
          </span>
        </h3>
      </div>

      <!-- <div class="participation__factor">
        <div class="participation__label">
          {{ t('efriends.participationFactorEEG') }}
        </div>
        <div class="participation__value">
          {{ eegParticipationFactor }}%
        </div>
      </div> -->
    </div>

    <div data-cy="eegConnections" class="producers__list">
      <transition-group name="producers__list-item" tag="div">
        <div v-for="cube in eegConnections" :key="cube.cubeName" class="producers__list-item" data-cy="producers-item">
          <Producer :producer="cube" />
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script lang="ts">
  import Producer from '@/components/efriends/Producer.vue';
  import { useStrings } from '@/composables/useStrings.ts';
  import { useUnits } from '@/composables/useUnits.ts';
  import { useDate } from '@/composables/useDate.ts';
  import { useCubeStore } from '@/stores/useCubeStore';
  import { useSettingsStore } from '@/stores/useSettingsStore';
  import { usePlans } from '@/composables/usePlans';
  import { computed } from 'vue';
  import { NumberUtil } from '@/util/NumberUtil';

  export default {
    name: 'producers',
    components: { Producer },
    setup() {
      const cubeStore = useCubeStore();
      const settingsStore = useSettingsStore();
      const { t } = useStrings();
      const { formatPowerPrice } = useUnits();
      const { formatPriceDate } = useDate();
      const { isBusinessPlan: checkIsBusinessPlan, isFlexPlan: checkIsFlexPlan } = usePlans();

      const con = computed(() => settingsStore.contractData.plans.con);

      const cubeDetails = computed(() => cubeStore.cube);
      const communityParticipationFactor = computed(() => NumberUtil.parse(cubeDetails.value?.customerInfo?.communityConsumptionParticipationFactor, 100)!);
      const eegParticipationFactor = computed(() => 100 - communityParticipationFactor.value);

      const allProducers = computed(() => cubeStore.producers.filter(p => !p.isContributor));
      const eegConnections = computed(() => allProducers.value.filter(p => p.productionType == 'eeg'));
      const hasEegConnections = computed(() => eegConnections.value?.length > 0);

      return {
        t,
        hasEegConnections,
        formatPowerPrice,
        formatPriceDate,
        allProducers,
        producers: computed(() => allProducers.value.filter(p => p.productionType != 'eeg')),
        eegConnections,
        showResidue: computed(() => cubeStore.supplier.name === 'eFriends Energy GmbH'),
        residueSupplier: computed(() => cubeStore.supplier.name),
        consumptionResidueVatPrice: computed(() => con.value?.vatPrice),
        nextConsumptionResidueVatPrice: computed(() => con.value?.nextVatPrice),
        consumptionResiduePrice: computed(() => con.value?.standardPrice),
        nextConsumptionResiduePrice: computed(() => con.value?.nextPrice),
        isBusinessPlan: computed(() => checkIsBusinessPlan()),
        isFlexPlan: computed(() => checkIsFlexPlan()),
        planName: computed(() => con.value?.name?.replace(/P01_3[0-9]{6}/, '')?.replaceAll('_', ' ')),
        communityParticipationFactor,
        eegParticipationFactor,
        nextPriceLabel: computed(() => {
          const date = new Date(new Date(con.value?.nextPriceValidFrom!).getTime() + 2 * 60 * 60 * 1000);
          return `Ab ${date ? formatPriceDate(date) : ''}`;
        }),
      };
    },
  };
</script>

<style lang="scss">
  .producers {

    &__message {
      box-shadow: 2px 2px 7px rgba(0, 0, 0, 0.15);
      padding: $spacer10 $spacer30 $spacer10 $spacer20;
      border-left: 4px solid $community;
      background: $white;
      margin: $spacer20 auto;
      max-width: 800px;

      @media (min-width: $bp-ml) {
        flex-basis: 100%;
      }

      .icon {
        width: 1em;
        display: inline-block;
        vertical-align: middle;
        margin-left: 0.2em;
        position: relative;
        top: -0.2em;
      }
    }
  }
</style>
